import { useCallback, useEffect, useState } from 'react';
import { updateMultiParameterCategories } from './updateMultiParameterCategories.ts';

/**
 * Hook to defer field update calls to avoid setState-inside-setState issues.
 *
 * When a component calls setState and then immediately calls onFieldUpdate (which may trigger
 * another setState), React can throw "Maximum update depth exceeded" errors. This hook defers
 * the onFieldUpdate call using useEffect, ensuring it runs after the current setState completes.
 *
 * @template State - The state type to be updated
 * @template Api - The API type returned by mapStateToApi
 * @template Field - The field type (usually a string or symbol)
 *
 * @param field - The field identifier to pass to onFieldUpdate
 * @param onFieldUpdate - Callback to update the field with the mapped API value
 * @param mapStateToApi - Function to transform state to API format
 * @param deps - Additional dependencies for the useEffect (beyond field and onFieldUpdate)
 *
 * @returns A function to schedule a deferred update with the given state
 *
 * @example
 * ```tsx
 * const scheduleDeferredUpdate = useDeferredFieldUpdate(
 *   field,
 *   onFieldUpdate,
 *   mapStateToApi
 * );
 *
 * // Later, in a setState callback:
 * setState(prev => {
 *   const next = {...prev, updated: true};
 *   scheduleDeferredUpdate(next);
 *   return next;
 * });
 * ```
 */
export function useDeferredFieldUpdate<State, Api, Field>(
  field: Field,
  onFieldUpdate: (field: Field, value: Api | null) => void,
  mapStateToApi: (state: State) => Api | null,
  deps: ReadonlyArray<unknown> = []
): (state: State) => void {
  // Use useState instead of useRef to ensure the effect is triggered
  const [pendingUpdate, setPendingUpdate] = useState<{ state: State } | null>(null);

  // Effect to handle deferred field updates
  useEffect(() => {
    if (pendingUpdate) {
      const { state } = pendingUpdate;
      setPendingUpdate(null);
      updateMultiParameterCategories(field, onFieldUpdate, state, mapStateToApi);
    }
  }, [pendingUpdate, field, onFieldUpdate, mapStateToApi, ...deps]);

  return useCallback((state: State) => {
    setPendingUpdate({ state });
  }, []);
}
